import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../contexts/user.context";
import {
  Get,
  handleError,
} from "../../../utils/fetch.util";
import AddSecurityTaskModal from "../../../components/modals/AddSecurityTaskModal/AddSecurityTaskModal.component";
import ConfirmationModal from "../../../components/modals/confirmationModal/confirmationModal.component";
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from "react-icons/fi";
import NoData from "../../../components/noData/noData.component";
import { useParams } from "react-router-dom";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import { Link } from "react-router-dom";
import { formatDate } from "date-fns";
import i18next from "i18next";
import Image from "../../../components/Image/Image.component";
import PreviewImage from "../GroupChats/PreviewImage.component";

interface SecurityTaskAnswer {
  formId: string;
  createdAt: string;
  user: {
    asdc_client_user_id: number;
    asdc_client_user_name: string;
    profile_pic: string;
    deleted?: boolean;
  };
  report: {
    report_id: number;
    report_title: string;
    versionId: string;
    version: string;
  };
}

const SecurityTaskAnswers = () => {
  const { taskId } = useParams();
  const { t } = useTranslation();
  const { getValidToken } = useContext(UserContext);
  const [answers, setAnswers] = useState<SecurityTaskAnswer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [filters, setFilters] = useState<{ search: string }>({ search: "" });
  const [pagination, setPagination] = useState<{
    page: number;
    pageItemsCount: number;
    count: number;
  }>({ page: 1, pageItemsCount: 10, count: 0 });

  const fetchAnswers = async () => {
    setIsLoading(true);
    const query: string[] = [];
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);
    query.push(`task_id=${taskId}`);
    const url = new URL(window.location.href);
    const createdAtFromUrl = url.searchParams.get("createdAt");
    if (createdAtFromUrl) query.push(`start_date=${createdAtFromUrl}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);
    const res = await Get(
      `security-post-task/get-security-post-task-answers?${query.join("&")}`,
      getValidToken()
    );
    if (res?.error || !res?.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }
    setAnswers(res.data.securityPostTaskAnswers);
    setPagination((prev) => ({ ...prev, count: res.data.count }));
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAnswers();
    // eslint-disable-next-line
  }, [pagination.page, filters.search]);

  return (
    <>
      <div className="container bg-white p-6 px-8">
        <div className="flex items-center gap-3 mb-5 justify-between">
          <div className="flex items-center gap-3">
            <BackButton />
            <h1 className="text-5xl">{t("security_task_answers")}</h1>
          </div>
          <div className="relative w-44">
            <input
              type="text"
              placeholder={t("search")}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              className="input w-full rounded-full p-3 pr-10"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setFilters({
                    ...filters,
                    search: (e.target as HTMLInputElement).value,
                  });
                  setPagination((p) => ({ ...p, page: 1 }));
                }
              }}
            />
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
              }}
              onClick={() => {
                setFilters({ ...filters, search: filters.search });
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              aria-label="Search"
              tabIndex={-1}
              type="button"
            >
              <FiSearch size={20} />
            </button>
          </div>
        </div>
        {answers.length === 0 ? (
          <NoData message={t("no_data_found")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-fixed rounded-md border-collapse w-full ">
              <thead>
                <tr>
                  <th className="border-2 p-4 py-2">{t("user")}</th>
                  <th className="border-2 p-4 py-2">{t("report_id")}</th>
                  <th className="border-2 p-4 py-2">{t("report_title")}</th>
                  <th className="border-2 p-4 py-2">{t("date")}</th>
                  <th className="border-2 p-4 py-2">{t("answer_form")}</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((answer) => (
                  <tr key={answer.formId}>
                    <td className="border-2 p-4 py-2">
                      <div
                        data-tooltip={answer.user.asdc_client_user_name}
                        className={`w-16 h-16 rounded-full object-contain custom-tooltip hover:before:content-[attr(data-tooltip)]
                          ${i18next.language === "ar" ? "-ml-3" : "-mr-3"} ${
                            answer.user?.deleted && "relative"
                          }`}
                        onClick={() => setPreviewImage(answer.user.profile_pic)}
                      >
                        <Image
                          className={`max-w-[90%] h-[90%] object-cover rounded-full cursor-pointer`}
                          src={answer.user.profile_pic}
                          alt={answer.user.asdc_client_user_name}
                        />
                        {answer.user?.deleted && (
                          <span className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-center text-white text-xs font-bold rounded-full">
                            {t("deleted")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="border-2 p-4 py-2">
                      {answer.report.report_id}
                    </td>
                    <td className="border-2 p-4 py-2">
                      {answer.report.report_title}
                    </td>
                    <td className="border-2 p-4 py-2">
                      {formatDate(answer.createdAt, "dd/MM/yyyy")}
                    </td>
                    <td className="border-2 p-4 py-2">
                      <Link
                        to={`/reportsAnswers/form/${answer.formId}?report_id=${answer.report.report_id}&versionId=${answer.report.versionId}&version=${answer.report.version}`}
                        className="text-primary"
                      >
                        {t("view")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {previewImage && (
        <PreviewImage src={previewImage} setSrc={setPreviewImage} />
      )}
    </>
  );
};

export default SecurityTaskAnswers;
